import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import GoogleMapReact from "google-map-react";
import { API_URL1 } from "../services/url";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  LinearProgress,
  Button,
} from "@material-ui/core";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RefreshIcon from "@mui/icons-material/Refresh";
import "./TruckerAllOrdersMap.css";


function TruckerAllOrdersMap() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [center, setCenter] = useState({ lat: 32.7458, lng: -111.6859 });
  const [zoom, setZoom] = useState(10);
  const [mapRef, setMapRef] = useState(null);
  const [mapsRef, setMapsRef] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindows, setInfoWindows] = useState([]);
  const refreshIntervalRef = useRef(null);
  const isViewingDetailsRef = useRef(false);

  // Track when user is viewing details
  useEffect(() => {
    isViewingDetailsRef.current = !!selectedOrder;
  }, [selectedOrder]);

  useEffect(() => {
    fetchOrders();
    // Refresh every 30 seconds for real-time updates (less frequent to avoid interrupting user)
    refreshIntervalRef.current = setInterval(() => {
      // Only refresh if user is not viewing order details
      if (!isViewingDetailsRef.current) {
        fetchOrders();
      }
    }, 30000); // 30 seconds instead of 5 seconds
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(
        `${API_URL1}order/mapSection/showmapTrucker`,
        {
          headers: {
            "x-access-token": localStorage.getItem("TruckertokenKey"),
            "content-type": "application/json",
          },
        }
      );

      if (response.data.success && response.data.order) {
        console.log("📦 Orders received from API:", response.data.order.length);
        
        const ordersWithLocation = response.data.order.map((order) => {
          // Parse last_location from API response (now included directly)
          let lastLocation = null;
          
          // Handle last_location from API
          if (order.last_location) {
            try {
              let parsedLocation = null;
              
              // If it's a string, parse it as JSON
              if (typeof order.last_location === 'string') {
                try {
                  parsedLocation = JSON.parse(order.last_location);
                } catch (parseError) {
                  console.error("Failed to parse location string:", parseError);
                }
              } 
              // If it's already an object, use it directly
              else if (order.last_location && typeof order.last_location === 'object') {
                parsedLocation = order.last_location;
              }
              
              // Extract lat/lng from parsed location
              if (parsedLocation) {
                const lat = parsedLocation.lat || parsedLocation.latitude || null;
                const lng = parsedLocation.lng || parsedLocation.longitude || null;
                
                // Only set lastLocation if we have valid coordinates
                if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
                  lastLocation = {
                    lat: parseFloat(lat),
                    lng: parseFloat(lng),
                    remaining_distance: parsedLocation.remaining_distance || 0,
                    estimated_time: parsedLocation.estimated_time || 0,
                  };
                  console.log("✅ Location parsed for order:", order.sub_job_truck_id, lastLocation);
                } else {
                  console.warn("⚠️ Invalid coordinates for order:", order.sub_job_truck_id, { lat, lng });
                }
              }
            } catch (e) {
              console.error("❌ Error parsing last_location:", e, order.last_location);
              lastLocation = null;
            }
          } else {
            console.warn("⚠️ No last_location for order:", order.sub_job_truck_id);
          }
          
          return { ...order, lastLocation };
        });
        
        console.log(`📍 Orders with location: ${ordersWithLocation.filter(o => o.lastLocation).length} / ${ordersWithLocation.length}`);
        
        // Preserve selectedOrder if it still exists in the new data
        setOrders(ordersWithLocation);
        
        // Update selectedOrder if it still exists (preserve user's selection during refresh)
        if (selectedOrder) {
          const updatedSelectedOrder = ordersWithLocation.find(
            o => o.sub_job_truck_id === selectedOrder.sub_job_truck_id
          );
          if (updatedSelectedOrder) {
            setSelectedOrder(updatedSelectedOrder);
          }
        }
        
        // Calculate bounds for all orders with locations
        const ordersWithValidLocation = ordersWithLocation.filter(o => o.lastLocation?.lat && o.lastLocation?.lng);
        if (ordersWithValidLocation.length > 0) {
          // Calculate bounds to fit all trucks
          const lats = ordersWithValidLocation.map(o => parseFloat(o.lastLocation.lat));
          const lngs = ordersWithValidLocation.map(o => parseFloat(o.lastLocation.lng));
          
          const minLat = Math.min(...lats);
          const maxLat = Math.max(...lats);
          const minLng = Math.min(...lngs);
          const maxLng = Math.max(...lngs);
          
          // Set center to midpoint of all locations
          setCenter({
            lat: (minLat + maxLat) / 2,
            lng: (minLng + maxLng) / 2,
          });
          
          // Auto-adjust zoom based on bounds spread
          const latDiff = maxLat - minLat;
          const lngDiff = maxLng - minLng;
          const maxDiff = Math.max(latDiff, lngDiff);
          
          if (maxDiff > 0.1) setZoom(9);
          else if (maxDiff > 0.05) setZoom(10);
          else if (maxDiff > 0.01) setZoom(12);
          else setZoom(14);
        } else {
          // Fallback to default location
          const firstOrderWithLocation = ordersWithLocation.find(o => o.lastLocation?.lat && o.lastLocation?.lng);
          if (firstOrderWithLocation && firstOrderWithLocation.lastLocation) {
            setCenter({
              lat: firstOrderWithLocation.lastLocation.lat,
              lng: firstOrderWithLocation.lastLocation.lng,
            });
          }
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setLoading(false);
    }
  };

  const handleApiLoaded = ({ map, maps }) => {
    console.log("🗺️ Google Maps API loaded", { map, maps });
    setMapRef(map);
    setMapsRef(maps);
  };


  useEffect(() => {
    console.log("🔍 Marker Effect Running:", { 
      hasMapRef: !!mapRef, 
      hasMapsRef: !!mapsRef, 
      ordersCount: orders.length,
      ordersWithLocation: orders.filter(o => o.lastLocation).length 
    });
    
    if (!mapRef || !mapsRef) {
      console.log("⏳ Waiting for map to load...");
      return;
    }
    
    if (orders.length === 0) {
      console.log("📭 No orders to display");
      return;
    }

    // Clear existing markers and info windows
    console.log(`🧹 Clearing ${markers.length} existing markers`);
    markers.forEach((marker) => {
      if (marker && marker.setMap) {
        marker.setMap(null);
      }
    });
    
    infoWindows.forEach((infoWindow) => {
      if (infoWindow && infoWindow.close) {
        infoWindow.close();
      }
    });

    const newMarkers = [];
    const newInfoWindows = [];
    const boundsObj = new mapsRef.LatLngBounds();
    
    // Track positions to handle overlapping markers
    const positionMap = new Map();
    
    console.log("🚚 Processing orders for markers...");
    orders.forEach((order, index) => {
      console.log(`Order ${index}:`, {
        sub_job_truck_id: order.sub_job_truck_id,
        hasLastLocation: !!order.lastLocation,
        lastLocation: order.lastLocation
      });
      
      if (order.lastLocation && order.lastLocation.lat && order.lastLocation.lng) {
        let lat = parseFloat(order.lastLocation.lat);
        let lng = parseFloat(order.lastLocation.lng);
        
        // Check if this position already exists (overlapping markers)
        const positionKey = `${lat.toFixed(6)}_${lng.toFixed(6)}`;
        if (positionMap.has(positionKey)) {
          // Offset overlapping markers slightly so they're visible
          const offsetCount = positionMap.get(positionKey);
          const offsetLat = lat + (offsetCount * 0.0001); // Small offset ~11 meters
          const offsetLng = lng + (offsetCount * 0.0001);
          lat = offsetLat;
          lng = offsetLng;
          positionMap.set(positionKey, offsetCount + 1);
          console.log(`⚠️ Overlapping marker detected! Offset applied: (${lat}, ${lng})`);
        } else {
          positionMap.set(positionKey, 1);
        }
        
        console.log(`✅ Creating marker for order ${order.sub_job_truck_id} at (${lat}, ${lng})`);
        
        // Add to bounds
        boundsObj.extend({ lat, lng });

        // Create truck icon with different color for each truck (embedded SVG - reliable, no external URLs)
        let iconMarker;
        try {
          // Different colors for different trucks - cycling through a vibrant color palette
          const truckColors = [
            '#2A707D', // Teal
            '#FF9800', // Orange
            '#4CAF50', // Green
            '#F44336', // Red
            '#9C27B0', // Purple
            '#00BCD4', // Cyan
            '#FF5722', // Deep Orange
            '#3F51B5', // Indigo
            '#E91E63', // Pink
            '#795548', // Brown
            '#607D8B', // Blue Grey
            '#FFC107', // Amber
          ];
          
          // Use index to cycle through colors, ensuring each truck gets a unique color
          const truckColor = truckColors[index % truckColors.length];
          
          // Create truck SVG icon with unique color
          const truckSVG = `
            <svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
              <!-- Truck body -->
              <rect x="6" y="18" width="24" height="16" fill="${truckColor}" stroke="#FFFFFF" stroke-width="2" rx="1"/>
              <!-- Truck cabin -->
              <rect x="30" y="20" width="12" height="14" fill="${truckColor}" stroke="#FFFFFF" stroke-width="2" rx="1"/>
              <!-- Cabin window -->
              <rect x="32" y="22" width="4" height="4" fill="#87CEEB" stroke="#000" stroke-width="0.5"/>
              <rect x="37" y="22" width="4" height="4" fill="#87CEEB" stroke="#000" stroke-width="0.5"/>
              <!-- Front wheels -->
              <circle cx="13" cy="34" r="3.5" fill="#333333"/>
              <circle cx="13" cy="34" r="2" fill="#666666"/>
              <!-- Back wheels -->
              <circle cx="33" cy="34" r="3.5" fill="#333333"/>
              <circle cx="33" cy="34" r="2" fill="#666666"/>
              <!-- Headlights -->
              <circle cx="42" cy="24" r="1.5" fill="#FFFF00"/>
              <circle cx="42" cy="30" r="1.5" fill="#FF0000"/>
            </svg>
          `;
          
          // Convert SVG to data URL (works offline, no external requests)
          const encodedSVG = encodeURIComponent(truckSVG);
          
          iconMarker = {
            url: `data:image/svg+xml;charset=utf-8,${encodedSVG}`,
            scaledSize: new mapsRef.Size(50, 50),
            anchor: new mapsRef.Point(25, 50), // Anchor at bottom center
            origin: new mapsRef.Point(0, 0),
          };
          
          console.log(`✅ Truck icon created for marker ${index} with color ${truckColor}`);
        } catch (e) {
          console.error(`❌ Error creating truck icon for marker ${index}:`, e);
          // Fallback to colored circle if SVG fails
          try {
            const truckColors = [
              '#2A707D', '#FF9800', '#4CAF50', '#F44336', '#9C27B0', '#00BCD4',
              '#FF5722', '#3F51B5', '#E91E63', '#795548', '#607D8B', '#FFC107',
            ];
            const truckColor = truckColors[index % truckColors.length];
            iconMarker = {
              path: mapsRef.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: truckColor,
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 3,
              anchor: new mapsRef.Point(0, 0),
            };
            console.log(`✅ Using fallback circle icon for marker ${index} with color ${truckColor}`);
          } catch (e2) {
            console.error(`❌ Fallback icon also failed for marker ${index}:`, e2);
            iconMarker = undefined; // Will use default Google marker
          }
        }

        // Create marker - always create even if icon fails
        let marker;
        try {
          const markerPosition = new mapsRef.LatLng(lat, lng);
          
          // Create marker - ALWAYS create, even if icon fails
          const markerOptions = {
            position: markerPosition,
            map: mapRef,
            title: `Truck: ${order.registration_number || "N/A"} - Driver: ${order.driver_name || "Driver"} - ${order.customer_site_name || "Site"}`,
            animation: mapsRef.Animation.DROP,
            zIndex: selectedOrder?.sub_job_truck_id === order.sub_job_truck_id ? 1000 : (100 + index),
            optimized: false,
            draggable: false,
            visible: true,
          };
          
          // Add custom icon if available, otherwise use default red marker
          if (iconMarker) {
            markerOptions.icon = iconMarker;
          }
          
          marker = new mapsRef.Marker(markerOptions);
          
          // Force marker to be visible
          marker.setVisible(true);
          
          console.log(`✅ Marker ${index + 1} created for order ${order.sub_job_truck_id} - ${iconMarker ? 'Custom truck icon' : 'Default icon'} at (${lat}, ${lng})`);

        // Create Info Window
        const infoWindowContent = `
          <div style="padding: 8px; min-width: 200px; font-family: Arial, sans-serif;">
            <h3 style="margin: 0 0 8px 0; color: #2A707D; font-size: 14px; font-weight: bold;">
              ${order.customer_site_name || "Site"}
            </h3>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Driver:</strong> ${order.driver_name || "N/A"}</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Truck:</strong> ${order.registration_number || "N/A"}</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Customer:</strong> ${order.customer_name || "N/A"}</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Material:</strong> ${order.size || "N/A"} - ${order.color_name || "N/A"}</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Quantity:</strong> ${order.processing_quantity ? parseFloat(order.processing_quantity).toFixed(2) + " Tons" : "N/A"}</p>
            ${order.lastLocation?.remaining_distance ? `<p style="margin: 4px 0; font-size: 12px; color: #4CAF50;"><strong>Distance:</strong> ${order.lastLocation.remaining_distance.toFixed(1)} miles</p>` : ""}
            ${order.lastLocation?.estimated_time ? `<p style="margin: 4px 0; font-size: 12px; color: #4CAF50;"><strong>ETA:</strong> ${order.lastLocation.estimated_time} min</p>` : ""}
            <p style="margin: 4px 0; font-size: 11px; color: #666; margin-top: 8px;">Click for more details</p>
          </div>
        `;
        
        const infoWindow = new mapsRef.InfoWindow({
          content: infoWindowContent,
        });

          // Show info window on marker click
          marker.addListener("click", () => {
            console.log("🖱️ Marker clicked:", order.driver_name);
            // Close all other info windows
            newInfoWindows.forEach((iw) => iw.close());
            
            // Open this info window
            infoWindow.open(mapRef, marker);
            
            setSelectedOrder(order);
            setCenter({ lat, lng });
            setZoom(15);
          });

          newMarkers.push(marker);
          newInfoWindows.push(infoWindow);
          
        } catch (markerError) {
          console.error(`❌ Error creating marker for order ${order.sub_job_truck_id}:`, markerError);
        }
      }
    });

    console.log(`🎯 Created ${newMarkers.length} markers out of ${orders.length} orders`);
    console.log("📍 All markers:", newMarkers.map((m, i) => ({
      index: i,
      position: m.getPosition()?.toJSON(),
      visible: m.getVisible(),
      title: m.getTitle(),
      map: m.getMap() === mapRef
    })));
    
    setMarkers(newMarkers);
    setInfoWindows(newInfoWindows);
    
    // Restore info window for selected order if it exists
    if (selectedOrder && mapRef) {
      const selectedMarker = newMarkers.find((marker, idx) => {
        const order = orders[idx];
        return order && order.sub_job_truck_id === selectedOrder.sub_job_truck_id;
      });
      if (selectedMarker) {
        const selectedInfoWindow = newInfoWindows.find((iw, idx) => {
          const order = orders[idx];
          return order && order.sub_job_truck_id === selectedOrder.sub_job_truck_id;
        });
        if (selectedInfoWindow && selectedMarker) {
          // Small delay to ensure marker is on map
          setTimeout(() => {
            selectedInfoWindow.open(mapRef, selectedMarker);
          }, 100);
        }
      }
    }

    // Auto-fit bounds if we have markers - wait for map to be fully ready
    if (newMarkers.length > 0) {
      // Use a longer delay to ensure map is fully initialized
      setTimeout(() => {
        try {
          console.log("🔧 Adjusting map view for markers...");
          
          if (newMarkers.length === 1) {
            // Single marker - center on it
            const singleMarker = newMarkers[0];
            const pos = singleMarker.getPosition();
            if (pos) {
              mapRef.setCenter(pos);
              mapRef.setZoom(15);
              console.log("📍 Centered map on single marker at:", pos.toJSON());
            }
          } else if (newMarkers.length > 1) {
            // Multiple markers - fit bounds
            const bounds = new mapsRef.LatLngBounds();
            let hasValidBounds = false;
            
            newMarkers.forEach(marker => {
              const pos = marker.getPosition();
              if (pos) {
                bounds.extend(pos);
                hasValidBounds = true;
              }
            });
            
            if (hasValidBounds) {
              // Check if bounds are too small (same location or very close)
              const ne = bounds.getNorthEast();
              const sw = bounds.getSouthWest();
              const latDiff = Math.abs(ne.lat() - sw.lat());
              const lngDiff = Math.abs(ne.lng() - sw.lng());
              
              // If markers are very close or at same location
              if (latDiff < 0.001 && lngDiff < 0.001) {
                // Center on first marker with appropriate zoom
                const firstMarker = newMarkers[0];
                const pos = firstMarker.getPosition();
                if (pos) {
                  mapRef.setCenter(pos);
                  mapRef.setZoom(16); // Zoom in enough to see overlapping markers
                  console.log("📍 Centered on overlapping markers at zoom 16");
                }
              } else {
                // Normal fit bounds for spread out markers
                mapRef.fitBounds(bounds, { padding: 100 });
                console.log("📍 Fitted bounds for multiple markers");
              }
            } else {
              // Fallback: use first marker
              const firstMarker = newMarkers[0];
              const pos = firstMarker.getPosition();
              if (pos) {
                mapRef.setCenter(pos);
                mapRef.setZoom(15);
              }
            }
          }
        } catch (e) {
          console.error("❌ Error adjusting map view:", e);
        }
      }, 1000); // Increased delay to 1 second to ensure map is ready
    } else {
      console.warn("⚠️ No markers were created! Check order.lastLocation data.");
      console.warn("Orders data:", orders.map(o => ({
        id: o.sub_job_truck_id,
        driver: o.driver_name,
        hasLocation: !!o.lastLocation,
        location: o.lastLocation
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, mapRef, mapsRef]);

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    if (order.lastLocation && order.lastLocation.lat && order.lastLocation.lng) {
      setCenter({
        lat: parseFloat(order.lastLocation.lat),
        lng: parseFloat(order.lastLocation.lng),
      });
      setZoom(15);
    }
  };

  // Get status color matching the image (yellow for pending, light blue for in progress, green for en route)
  const getStatusColor = (status, hasLocation) => {
    if (!hasLocation) return "#FFC107"; // Yellow for pending (location pending)
    if (status === 3) return "#42A5F5"; // Light blue for in progress (Dispatched)
    if (status === 4) return "#4CAF50"; // Green for en route (In Transit)
    return "#FFC107"; // Yellow for other pending states
  };

  const getStatusText = (status, hasLocation) => {
    if (!hasLocation) return "pending";
    if (status === 3) return "in progress";
    if (status === 4) return "en route";
    return "pending";
  };


  // Format date for display
  const formatDate = (order) => {
    // Try different possible date fields from order
    const dateValue = order.created_at || order.job_created_at || order.date || order.order_date || null;
    if (!dateValue) {
      // Fallback to current date format like "Oct 28, 2025"
      const today = new Date();
      return today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch (e) {
      // Fallback to current date if parsing fails
      const today = new Date();
      return today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
  };

  // Generate unique ID from order
  const generateOrderId = (order) => {
    // Use job_id or sub_job_truck_id to create ID like "RP01-11233F"
    const id = order.job_id || order.sub_job_truck_id || Math.random().toString(36).substr(2, 9);
    return `RP${String(id).slice(-5)}`;
  };

  return (
    <div className="all-orders-map-container">

      {loading ? (
        <LinearProgress style={{ marginTop: "16px" }} />
      ) : (
        <div className="all-orders-map-content">
          {/* Side Panel - Orders List */}
          <div className="orders-sidebar">
            <Box
              style={{
                padding: "20px",
                background: "white",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="h5" style={{ fontWeight: 600, color: "#333" }}>
                Active Deliveries
              </Typography>
            </Box>

            <div className="orders-list">
              {orders.length === 0 ? (
                <Box
                  style={{
                    padding: "32px",
                    textAlign: "center",
                    color: "#757575",
                  }}
                >
                  <LocationOnIcon style={{ fontSize: 48, opacity: 0.3 }} />
                  <Typography variant="h6" style={{ marginTop: "16px", fontWeight: 600 }}>
                    No Active Orders
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: "8px", marginBottom: "16px" }}>
                    Your assigned trucks will appear here once orders are dispatched
                  </Typography>
                </Box>
              ) : (
                orders.map((order, index) => (
                  <Card
                    key={index}
                    className={`order-card ${selectedOrder?.sub_job_truck_id === order.sub_job_truck_id ? "selected" : ""}`}
                    style={{
                      marginBottom: "16px",
                      borderRadius: "8px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <CardContent style={{ padding: "16px" }}>
                      {/* Customer Site Name */}
                      <Typography
                        variant="h6"
                        style={{
                          fontWeight: 600,
                          color: "#333",
                          marginBottom: "8px",
                        }}
                      >
                        {order.customer_site_name || "Delivery Site"}
                      </Typography>
                      
                      {/* Order ID */}
                      <Typography
                        variant="caption"
                        style={{
                          color: "#666",
                          marginBottom: "12px",
                          display: "block",
                        }}
                      >
                        {generateOrderId(order)}
                      </Typography>

                      {/* Status Badge */}
                      <Chip
                        label={getStatusText(order.job_status, !!order.lastLocation)}
                        size="small"
                        style={{
                          backgroundColor: getStatusColor(order.job_status, !!order.lastLocation),
                          color: "white",
                          fontWeight: 600,
                          marginBottom: "12px",
                          fontSize: "11px",
                          height: "24px",
                        }}
                      />

                      {/* Customer Name */}
                      <Typography variant="body2" style={{ color: "#666", fontSize: "13px", marginBottom: "6px" }}>
                        <strong>Customer:</strong> {order.customer_name || "N/A"}
                      </Typography>

                      {/* Driver Name */}
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <PersonIcon style={{ fontSize: 16, marginRight: "6px", color: "#757575" }} />
                        <Typography variant="body2" style={{ color: "#666", fontSize: "13px" }}>
                          <strong>Driver:</strong> {order.driver_name || "N/A"}
                        </Typography>
                      </Box>

                      {/* Truck Registration */}
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <LocalShippingIcon style={{ fontSize: 16, marginRight: "6px", color: "#757575" }} />
                        <Typography variant="body2" style={{ color: "#666", fontSize: "13px" }}>
                          <strong>Truck:</strong> {order.registration_number || "N/A"}
                        </Typography>
                      </Box>

                      {/* Material */}
                      <Typography variant="body2" style={{ color: "#666", fontSize: "13px", marginBottom: "6px" }}>
                        <strong>Material:</strong> {order.size || "N/A"} - {order.color_name || "N/A"}
                      </Typography>

                      {/* Quantity */}
                      <Typography variant="body2" style={{ color: "#666", fontSize: "13px", marginBottom: "12px" }}>
                        <strong>Quantity:</strong>{" "}
                        {order.processing_quantity
                          ? `${parseFloat(order.processing_quantity).toFixed(2)} Tons`
                          : "N/A"}
                      </Typography>

                      {/* Divider */}
                      <Box
                        style={{
                          height: "1px",
                          backgroundColor: "#e0e0e0",
                          marginBottom: "12px",
                        }}
                      />

                      {/* Location Status */}
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <LocationOnIcon style={{ fontSize: 16, marginRight: "6px", color: "#757575" }} />
                        <Typography variant="body2" style={{ color: "#666", fontSize: "13px" }}>
                          {order.lastLocation ? "Location active" : "Location pending"}
                        </Typography>
                      </Box>

                      {/* Date */}
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "12px",
                        }}
                      >
                        <CalendarTodayIcon style={{ fontSize: 16, marginRight: "6px", color: "#757575" }} />
                        <Typography variant="body2" style={{ color: "#666", fontSize: "13px" }}>
                          {formatDate(order)}
                        </Typography>
                      </Box>

                      {/* View Location Button */}
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => handleOrderClick(order)}
                        style={{
                          textTransform: "none",
                          borderColor: "#2A707D",
                          color: "#2A707D",
                          fontSize: "12px",
                          padding: "6px",
                        }}
                      >
                        View Location
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Map Container */}
          <div className="map-container">
            {/* Map Header Bar with Gradient */}
            <Box
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                background: "linear-gradient(135deg, #9C27B0 0%, #2196F3 100%)",
                color: "white",
                padding: "12px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                zIndex: 1000,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <Box>
                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: "2px" }}>
                  Interactive Map
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.9, fontSize: "13px" }}>
                  Tracking {orders.length} active deliver{orders.length !== 1 ? 'ies' : 'y'}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => {
                  setLoading(true);
                  fetchOrders();
                }}
                style={{
                  color: "white",
                  textTransform: "none",
                  minWidth: "auto",
                  padding: "6px 12px",
                }}
              >
                <RefreshIcon style={{ fontSize: 18, marginRight: "4px" }} />
                Refresh
              </Button>
            </Box>
            
            <div style={{ marginTop: "60px", height: "calc(100vh - 180px)", width: "100%" }}>
            <GoogleMapReact
              bootstrapURLKeys={{ key: process.env.REACT_APP_MAPS_API_KEY }}
              center={center}
              zoom={zoom}
              onGoogleApiLoaded={handleApiLoaded}
              yesIWantToUseGoogleMapApiInternals
              options={{
                fullscreenControl: true,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: true,
                styles: [
                  {
                    featureType: "poi",
                    elementType: "labels",
                    stylers: [{ visibility: "off" }],
                  },
                ],
              }}
            />
            </div>

            {/* Selected Order Info Overlay */}
            {selectedOrder && (
              <Card
                style={{
                  position: "absolute",
                  bottom: "80px",
                  left: "20px",
                  right: orders.length > 1 ? "400px" : "20px",
                  maxWidth: "400px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  zIndex: 1000,
                  background: "white",
                }}
              >
                <CardContent>
                  <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                    <Typography variant="h6" style={{ fontWeight: 600, color: "#2A707D" }}>
                      {selectedOrder.customer_site_name || "Order Details"}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setSelectedOrder(null)}
                      style={{ minWidth: "auto", padding: "4px" }}
                    >
                      ✕
                    </Button>
                  </Box>
                  <Box>
                    <Typography variant="body2" style={{ marginBottom: "8px" }}>
                      <strong>Customer:</strong> {selectedOrder.customer_name || "N/A"}
                    </Typography>
                    <Box style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                      <PersonIcon style={{ fontSize: 18, marginRight: "8px", color: "#757575" }} />
                      <Typography variant="body2">
                        <strong>Driver:</strong> {selectedOrder.driver_name || "N/A"}
                      </Typography>
                    </Box>
                    <Box style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                      <LocalShippingIcon style={{ fontSize: 18, marginRight: "8px", color: "#757575" }} />
                      <Typography variant="body2">
                        <strong>Truck:</strong> {selectedOrder.registration_number || "N/A"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" style={{ marginBottom: "8px" }}>
                      <strong>Material:</strong> {selectedOrder.size || "N/A"} - {selectedOrder.color_name || "N/A"}
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: "8px" }}>
                      <strong>Quantity:</strong>{" "}
                      {selectedOrder.processing_quantity
                        ? `${parseFloat(selectedOrder.processing_quantity).toFixed(2)} Tons`
                        : "N/A"}
                    </Typography>
                    {selectedOrder.lastLocation && (
                      <>
                        <Box style={{ marginTop: "12px", padding: "8px", background: "#e8f5e9", borderRadius: "6px" }}>
                          <Typography variant="body2" style={{ color: "#4CAF50", fontWeight: 600, marginBottom: "4px" }}>
                            <LocationOnIcon style={{ fontSize: 16, verticalAlign: "middle" }} /> Live Tracking Active
                          </Typography>
                          {selectedOrder.lastLocation.remaining_distance > 0 && (
                            <Typography variant="caption" style={{ color: "#2e7d32", display: "block" }}>
                              Distance: {selectedOrder.lastLocation.remaining_distance.toFixed(1)} miles
                            </Typography>
                          )}
                          {selectedOrder.lastLocation.estimated_time > 0 && (
                            <Typography variant="caption" style={{ color: "#2e7d32", display: "block" }}>
                              ETA: {selectedOrder.lastLocation.estimated_time} minutes
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TruckerAllOrdersMap;

